import { createLead, ApiError } from "../api";
import { pushNavState, onPopState, type NavState } from "../router";
import type {
  Collaborateurs,
  Secteur,
  ChiffreAffaires,
  ProblemeCategorie,
  MaturiteIa,
  MotifIneligibilite,
  LeadPayload,
  IneligibleLeadPayload,
  LeadCreatePayload,
} from "../types";

/**
 * Chargé de façon asynchrone par le script Cloudflare (voir index.html) : peut être
 * `undefined` un court instant après le chargement de la page, avant que le script externe
 * n'ait fini de s'exécuter.
 */
declare global {
  interface Window {
    turnstile?: {
      getResponse(container?: string | HTMLElement): string | undefined;
      reset(container?: string | HTMLElement): void;
    };
  }
}

/**
 * Lit le token du widget Turnstile identifié par l'id de son conteneur (chaîne vide si
 * absent/non complété).
 *
 * Source principale : le champ caché `<input name="cf-turnstile-response">` que Cloudflare
 * injecte lui-même dans le conteneur une fois le défi résolu — c'est le contrat documenté par
 * Cloudflare pour une intégration en rendu implicite (data-sitekey) hors `<form>` natif, donc
 * la source la plus fiable. `turnstile.getResponse(containerId)` sert de repli : en prod, ce
 * dernier renvoyait vide alors que le widget affichait pourtant "Succès !", d'où ce changement.
 * `getResponse` peut aussi *lever* (pas seulement renvoyer vide) si le widget n'a jamais fini de
 * s'initialiser — domaine non autorisé côté dashboard Cloudflare, bloqueur de pub, réseau
 * lent... : `try/catch` pour dégrader proprement vers "pas de token" plutôt que de planter.
 */
function getTurnstileToken(containerId: string): string {
  const hiddenInput = document.querySelector<HTMLInputElement>(`#${containerId} input[name="cf-turnstile-response"]`);
  if (hiddenInput?.value) return hiddenInput.value;

  try {
    return window.turnstile?.getResponse(containerId) ?? "";
  } catch {
    return "";
  }
}

/** Réinitialise un widget Turnstile — son token est à usage unique, à refaire après chaque tentative d'envoi. */
function resetTurnstileWidget(containerId: string): void {
  try {
    window.turnstile?.reset(containerId);
  } catch {
    // Rien à faire : le widget n'a de toute façon jamais réussi à s'initialiser (voir getTurnstileToken).
  }
}

const TOTAL_STEPS = 9;

/**
 * Étapes dont la réponse peut disqualifier l'entreprise (voir `getIneligibilityMotif`).
 * Sur ces étapes uniquement, aussi bien le clic sur une option (auto-avance) que le clic
 * explicite sur "Continuer" déclenchent la vérification d'éligibilité.
 */
const GATING_STEPS = new Set([1, 2, 3, 7]);

export interface FormState {
  collaborateurs?: Collaborateurs;
  wallonie?: boolean;
  chiffreAffaires?: ChiffreAffaires;
  secteur?: Secteur;
  secteurAutre?: string;
  problemeCategorie?: ProblemeCategorie;
  probleme?: string;
  maturiteIa?: MaturiteIa;
  maturiteDetail?: string;
  ouvertureAcces?: boolean;
}

export type ContactFields = Pick<LeadPayload, "nom" | "fonction" | "email" | "telephone" | "consentement">;

const state: FormState = {};

/** Remet toutes les réponses du parcours à zéro — appelé à chaque retour sur la page. */
function resetState(): void {
  state.collaborateurs = undefined;
  state.wallonie = undefined;
  state.chiffreAffaires = undefined;
  state.secteur = undefined;
  state.secteurAutre = undefined;
  state.problemeCategorie = undefined;
  state.probleme = undefined;
  state.maturiteIa = undefined;
  state.maturiteDetail = undefined;
  state.ouvertureAcces = undefined;
}

/**
 * Longueur minimale du besoin exprimé (étape 5, catégorie "Autre") — rejette les entrées
 * quasi vides ("a", "test") sans être un obstacle pour une vraie phrase courte. Alignée sur
 * la validation serveur (voir backend/src/schemas/lead.schema.ts).
 */
export const MIN_PROBLEME_LENGTH = 10;

/**
 * Lettres (dont accentuées FR/NL/DE), espaces, apostrophes (droites et typographiques) et
 * tirets — vérification légère côté client, doublée par la même règle côté serveur (seule
 * autorité réelle, voir README/backend). Sert à donner un retour immédiat, pas à sécuriser.
 */
const NAME_PATTERN = /^[A-Za-zÀ-ÿ'’ -]{2,80}$/;

/** Vérification de forme minimale — la validation faisant autorité reste côté serveur. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Numéro belge fixe ou mobile, conforme au plan de numérotation (indicatif de zone à 1
 * chiffre pour les grandes villes type Bruxelles/Anvers/Liège, 2 chiffres pour les zones
 * plus petites ; préfixes mobiles réels 04[60|7x|8x|9x]). Tolère +32/0032/0 et les
 * séparateurs espace/point/tiret tapés naturellement entre les groupes de chiffres.
 * Vérification de forme uniquement — la normalisation et la validation réelle
 * (libphonenumber-js, qui détecte aussi les numéros inexistants) restent côté serveur.
 */
const PHONE_PATTERN =
  /^(?:(?:\+|00)32[ .-]?|0)(?:4[ .-]?(?:60|[789]\d)(?:[ .-]?\d{2}){3}|(?:\d[ .-]?\d{3}|\d{2}[ .-]?\d{2})(?:[ .-]?\d{2}){2})$/;

/**
 * Détecte les marqueurs de balisage actif les plus courants — mêmes motifs que
 * `NO_ACTIVE_MARKUP_PATTERN` côté serveur (backend/src/schemas/lead.schema.ts, seule
 * autorité réelle). Ici uniquement pour donner un retour immédiat à l'utilisateur ; ne
 * bloque volontairement ni guillemets, ni `%`, ni `|`, ni aucun autre caractère de
 * ponctuation légitime dans un texte libre.
 */
const NO_ACTIVE_MARKUP_PATTERN = /<\s*(script|iframe|object|embed|style)\b|javascript\s*:|on\w+\s*=/i;

/**
 * Détermine si l'entreprise est inéligible à partir des réponses déjà connues, et pourquoi.
 * Miroir exact de la logique serveur (backend/src/schemas/lead.schema.ts) : sert uniquement
 * à piloter l'affichage (redirection immédiate vers l'écran d'inéligibilité) — le serveur
 * recalcule cette même décision de façon indépendante et ne fait jamais confiance au client.
 */
export function getIneligibilityMotif(state: FormState): MotifIneligibilite | null {
  if (state.collaborateurs === "plus250") return "taille";
  if (state.wallonie === false) return "hors_wallonie";
  if (state.chiffreAffaires === "plus50M") return "ca";
  if (state.ouvertureAcces === false) return "acces_refuse";
  return null;
}

const INELIGIBLE_COPY: Record<MotifIneligibilite, { title: string; reason: string }> = {
  hors_wallonie: {
    title: "Ce programme est réservé aux entreprises basées en Wallonie",
    reason:
      "Le financement Start IA est porté par la Région wallonne et n'est ouvert qu'aux entreprises ayant leur siège social ou une unité d'établissement en Wallonie.",
  },
  taille: {
    title: "Ce programme est réservé aux PME de moins de 250 collaborateurs",
    reason:
      "Le financement Start IA cible spécifiquement les petites et moyennes entreprises, telles que définies par la Région wallonne.",
  },
  ca: {
    title: "Ce programme est réservé aux entreprises de moins de 50 M€ de chiffre d'affaires",
    reason:
      "Le financement Start IA cible spécifiquement les petites et moyennes entreprises, telles que définies par la Région wallonne.",
  },
  acces_refuse: {
    title: "Cet accès est une condition du programme Start IA",
    reason:
      "Le cadrage Start IA nécessite un accès macro à votre organisation pour identifier vos cas d'usage prioritaires, dans le respect strict de vos données : l'objectif est de comprendre votre activité, pas d'auditer vos systèmes. Sans cet accès minimal, nous ne pouvons malheureusement pas mener ce cadrage à son terme.",
  },
};

/**
 * Valide l'état du formulaire avant envoi. Pure (aucun accès DOM) : renvoie le message
 * d'erreur à afficher, ou `null` si tout est valide.
 */
export function getFormValidationError(state: FormState, contact: ContactFields): string | null {
  if (
    !state.collaborateurs ||
    state.wallonie === undefined ||
    !state.chiffreAffaires ||
    !state.secteur ||
    !state.problemeCategorie ||
    !state.maturiteIa ||
    state.ouvertureAcces === undefined
  ) {
    return "Merci de compléter les questions précédentes avant d'envoyer votre demande.";
  }
  if (state.secteur === "autre" && !state.secteurAutre) {
    return "Merci de préciser votre secteur d'activité.";
  }
  if (state.problemeCategorie === "autre") {
    if (!state.probleme || state.probleme.length < MIN_PROBLEME_LENGTH) {
      return `Merci de décrire votre besoin en au moins ${MIN_PROBLEME_LENGTH} caractères.`;
    }
    if (NO_ACTIVE_MARKUP_PATTERN.test(state.probleme)) {
      return "Le champ \"besoin\" ne peut pas contenir de balise ou de contenu actif (script, iframe...).";
    }
  }
  if (!contact.nom || !NAME_PATTERN.test(contact.nom)) {
    return "Le nom doit contenir entre 2 et 80 caractères (lettres, espaces, apostrophes et tirets uniquement).";
  }
  if (contact.fonction && NO_ACTIVE_MARKUP_PATTERN.test(contact.fonction)) {
    return "Le champ \"fonction\" ne peut pas contenir de balise ou de contenu actif (script, iframe...).";
  }
  if (!contact.email || !EMAIL_PATTERN.test(contact.email)) {
    return "L'email professionnel est requis et doit être valide.";
  }
  if (!contact.telephone || !PHONE_PATTERN.test(contact.telephone)) {
    return "Merci d'indiquer un numéro de téléphone belge valide (ex : 04XX XX XX XX ou +32 4XX XX XX XX).";
  }
  if (!contact.consentement) {
    return "Merci d'accepter le traitement de vos données pour continuer.";
  }
  return null;
}

/**
 * Construit le payload envoyé à l'API à partir de l'état du formulaire et des coordonnées.
 * Suppose que `getFormValidationError` a déjà validé `state`/`contact` — lève une erreur
 * explicite sinon, plutôt que d'envoyer un payload partiel à l'API.
 */
export function buildLeadPayload(state: FormState, contact: ContactFields): LeadPayload {
  if (
    !state.collaborateurs ||
    state.wallonie === undefined ||
    !state.chiffreAffaires ||
    !state.secteur ||
    !state.problemeCategorie ||
    !state.maturiteIa ||
    state.ouvertureAcces === undefined
  ) {
    throw new Error("buildLeadPayload appelé avec un état de formulaire incomplet");
  }
  return {
    collaborateurs: state.collaborateurs,
    wallonie: state.wallonie,
    chiffreAffaires: state.chiffreAffaires,
    secteur: state.secteur,
    ...(state.secteurAutre !== undefined ? { secteurAutre: state.secteurAutre } : {}),
    problemeCategorie: state.problemeCategorie,
    ...(state.probleme !== undefined ? { probleme: state.probleme } : {}),
    maturiteIa: state.maturiteIa,
    ...(state.maturiteDetail !== undefined ? { maturiteDetail: state.maturiteDetail } : {}),
    ouvertureAcces: state.ouvertureAcces,
    ...contact,
  };
}

/**
 * Valide le mini-formulaire de contact de l'écran d'inéligibilité (email + consentement
 * seulement). Pure (aucun accès DOM), même style que `getFormValidationError`.
 */
export function getIneligibleFormValidationError(email: string, consentement: boolean): string | null {
  if (!email || !EMAIL_PATTERN.test(email)) {
    return "L'email professionnel est requis et doit être valide.";
  }
  if (!consentement) {
    return "Merci d'accepter le traitement de vos données pour continuer.";
  }
  return null;
}

/**
 * Construit le payload minimal envoyé pour un lead inéligible : seules les réponses
 * effectivement données avant la redirection sont incluses (les clés absentes du parcours
 * sont omises plutôt qu'envoyées à `undefined`, pour rester compatible avec `.strict()`
 * côté serveur). `ouvertureAcces` est inclus dès qu'il est connu (motif 'acces_refuse') :
 * le serveur en a besoin pour recalculer lui-même ce motif de façon indépendante.
 */
export function buildIneligibleLeadPayload(
  state: FormState,
  email: string,
  consentement: boolean
): IneligibleLeadPayload {
  if (!state.collaborateurs) {
    throw new Error("buildIneligibleLeadPayload appelé sans collaborateurs connu");
  }
  const payload: IneligibleLeadPayload = {
    collaborateurs: state.collaborateurs,
    email,
    consentement,
  };
  if (state.wallonie !== undefined) payload.wallonie = state.wallonie;
  if (state.chiffreAffaires !== undefined) payload.chiffreAffaires = state.chiffreAffaires;
  if (state.secteur !== undefined) payload.secteur = state.secteur;
  if (state.secteurAutre !== undefined) payload.secteurAutre = state.secteurAutre;
  if (state.problemeCategorie !== undefined) payload.problemeCategorie = state.problemeCategorie;
  if (state.probleme !== undefined) payload.probleme = state.probleme;
  if (state.maturiteIa !== undefined) payload.maturiteIa = state.maturiteIa;
  if (state.maturiteDetail !== undefined) payload.maturiteDetail = state.maturiteDetail;
  if (state.ouvertureAcces !== undefined) payload.ouvertureAcces = state.ouvertureAcces;
  return payload;
}

function setProgress(step: number): void {
  const fill = document.getElementById("progressFill");
  if (fill) {
    fill.style.width = `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%`;
  }
  document.getElementById("progressTrack")?.setAttribute("aria-valuenow", String(step));
}

function showFormStep(step: number): void {
  const steps = Array.from(document.querySelectorAll<HTMLElement>(".fstep"));
  steps.forEach((el) => {
    el.classList.toggle("active", Number(el.dataset.step) === step);
  });
  setProgress(step);

  // Déplace le focus vers l'étape affichée : annonce le changement d'étape aux lecteurs
  // d'écran et repositionne les utilisateurs clavier (chaque .fstep a tabindex="-1").
  const activeStep = steps.find((el) => Number(el.dataset.step) === step);
  activeStep?.focus({ preventScroll: true });
}

/**
 * Affiche l'écran d'inéligibilité à la place du parcours numéroté, avec le titre/texte
 * adaptés au motif du rejet. La barre de progression reste figée à son état actuel plutôt
 * que d'être recalculée : cet écran n'a pas de position dans le parcours 1-9. Le lien
 * "Revenir à la question précédente" n'a de sens que pour le motif 'acces_refuse' (un choix
 * de volonté, pas une contrainte objective comme les 3 autres) : masqué sinon.
 */
function showIneligibleScreen(motif: MotifIneligibilite): void {
  const copy = INELIGIBLE_COPY[motif];
  const titleEl = document.getElementById("ineligibleTitle");
  const reasonEl = document.getElementById("ineligibleReason");
  if (titleEl) titleEl.textContent = copy.title;
  if (reasonEl) reasonEl.textContent = copy.reason;

  document.getElementById("ineligibleBackToForm")?.classList.toggle("visually-hidden", motif !== "acces_refuse");

  const steps = Array.from(document.querySelectorAll<HTMLElement>(".fstep"));
  steps.forEach((el) => {
    el.classList.toggle("active", el.dataset.step === "ineligible");
  });
  const ineligibleStep = steps.find((el) => el.dataset.step === "ineligible");
  ineligibleStep?.focus({ preventScroll: true });
}

/**
 * Avance à l'étape suivante et empile une entrée d'historique — le bouton "Précédent" du
 * navigateur (et le bouton "Retour" du formulaire, voir `bindNavButtons`, qui appelle
 * directement `history.back()`) y ramène alors sans logique dupliquée.
 */
function nextStep(current: number): void {
  const step = current + 1;
  showFormStep(step);
  pushNavState({ page: "formulaire", step });
}

function bindOptionGroups(): void {
  document.querySelectorAll<HTMLElement>(".options").forEach((group) => {
    const field = group.dataset.field;
    group.querySelectorAll<HTMLInputElement>(".opt-input").forEach((input) => {
      // "change" couvre toute méthode de sélection (clic, tactile, flèches clavier au sein
      // du groupe radio natif) : l'état du formulaire reste toujours correct.
      input.addEventListener("change", () => {
        const value = input.value;
        if (field === "collaborateurs") state.collaborateurs = value as Collaborateurs;
        if (field === "wallonie") state.wallonie = value === "oui";
        if (field === "chiffreAffaires") state.chiffreAffaires = value as ChiffreAffaires;
        if (field === "secteur") state.secteur = value as Secteur;
        if (field === "problemeCategorie") state.problemeCategorie = value as ProblemeCategorie;
        if (field === "maturiteIa") state.maturiteIa = value as MaturiteIa;
        if (field === "ouvertureAcces") state.ouvertureAcces = value === "oui";

        // Bascule l'affichage des champs complémentaires liés à ce groupe d'options (secteur
        // "Autre", problème "Autre", niveaux de maturité IA avec suivi) : un seul visible à
        // la fois, celui de l'option actuellement sélectionnée.
        group.querySelectorAll<HTMLInputElement>(".opt-input[data-reveal]").forEach((revealInput) => {
          const boxId = revealInput.dataset.reveal;
          if (boxId) {
            document.getElementById(boxId)?.classList.toggle("visually-hidden", revealInput.value !== value);
          }
        });
      });

      // "click" ne se déclenche pas lors d'une simple navigation au clavier (flèches) —
      // seulement sur une activation délibérée (clic, tactile, espace) — donc l'avance
      // automatique ne surprend pas les utilisateurs clavier qui parcourent les options.
      input.addEventListener("click", () => {
        // Une option qui révèle un champ complémentaire ne doit pas avancer automatiquement :
        // l'utilisateur doit d'abord pouvoir le remplir, puis cliquer "Continuer".
        if (input.dataset.reveal) return;

        const stepEl = input.closest<HTMLElement>(".fstep");
        const stepNum = Number(stepEl?.dataset.step ?? 0);
        window.setTimeout(() => {
          if (stepNum <= 0) return;
          if (GATING_STEPS.has(stepNum)) {
            const motif = getIneligibilityMotif(state);
            if (motif) {
              showIneligibleScreen(motif);
              pushNavState({ page: "formulaire", ineligible: motif });
              return;
            }
          }
          if (stepNum < TOTAL_STEPS - 1) nextStep(stepNum);
        }, 220);
      });
    });
  });
}

/** Ajoute/retire le style "champ invalide" (bordure rouge) et l'état ARIA associé. */
function setFieldInvalid(field: HTMLElement | null, invalid: boolean): void {
  if (!field) return;
  field.classList.toggle("field-invalid", invalid);
  if (invalid) {
    field.setAttribute("aria-invalid", "true");
  } else {
    field.removeAttribute("aria-invalid");
  }
}

/** Efface la bordure rouge dès que l'utilisateur recommence à modifier un champ marqué invalide. */
function clearFieldInvalidOnInput(id: string): void {
  document.getElementById(id)?.addEventListener("input", () => {
    setFieldInvalid(document.getElementById(id), false);
  });
}

/** Étape 4 : secteur d'activité, avec précision libre obligatoire si "Autre" est choisi. */
function validateSecteurStep(): boolean {
  if (!state.secteur) {
    showError("Merci de sélectionner un secteur d'activité.");
    return false;
  }
  if (state.secteur === "autre") {
    const input = document.getElementById("secteur-autre") as HTMLInputElement | null;
    const value = input?.value.trim() ?? "";
    if (value.length < 2) {
      showError("Merci de préciser votre secteur d'activité.");
      setFieldInvalid(input, true);
      input?.focus();
      return false;
    }
    if (NO_ACTIVE_MARKUP_PATTERN.test(value)) {
      showError("Ce champ ne peut pas contenir de balise ou de contenu actif (script, iframe...).");
      setFieldInvalid(input, true);
      input?.focus();
      return false;
    }
    setFieldInvalid(input, false);
    state.secteurAutre = value;
  } else {
    state.secteurAutre = undefined;
  }
  clearError();
  return true;
}

/**
 * Étape 5 : catégorie de problème, avec description libre obligatoire (≥ MIN_PROBLEME_LENGTH
 * caractères) uniquement si "Pas encore identifié (Autre)" est choisi.
 */
function validateProblemeStep(): boolean {
  if (!state.problemeCategorie) {
    showError("Merci de sélectionner un problème.");
    return false;
  }
  if (state.problemeCategorie === "autre") {
    const textarea = document.getElementById("besoin") as HTMLTextAreaElement | null;
    const value = textarea?.value.trim() ?? "";
    if (value.length < MIN_PROBLEME_LENGTH) {
      showError(`Merci de décrire votre besoin en au moins ${MIN_PROBLEME_LENGTH} caractères.`);
      setFieldInvalid(textarea, true);
      textarea?.focus();
      return false;
    }
    if (NO_ACTIVE_MARKUP_PATTERN.test(value)) {
      showError("Ce champ ne peut pas contenir de balise ou de contenu actif (script, iframe...).");
      setFieldInvalid(textarea, true);
      textarea?.focus();
      return false;
    }
    setFieldInvalid(textarea, false);
    state.probleme = value;
  } else {
    state.probleme = undefined;
  }
  clearError();
  return true;
}

/**
 * Étape 6 : niveau de maturité IA, avec précision libre facultative (non bloquante — sert à
 * évaluer le potentiel du client, pas à qualifier son éligibilité) pour les niveaux
 * "Connaissance avérée" et "Expérience concrète".
 */
function validateMaturiteStep(): boolean {
  if (!state.maturiteIa) {
    showError("Merci de sélectionner votre niveau de maturité avec l'IA.");
    return false;
  }
  let detailField: HTMLTextAreaElement | null = null;
  if (state.maturiteIa === "avere") {
    detailField = document.getElementById("maturite-detail-avere") as HTMLTextAreaElement | null;
  } else if (state.maturiteIa === "experience") {
    detailField = document.getElementById("maturite-detail-experience") as HTMLTextAreaElement | null;
  }
  const detailValue = detailField?.value.trim() || undefined;
  if (detailValue && NO_ACTIVE_MARKUP_PATTERN.test(detailValue)) {
    showError("Ce champ ne peut pas contenir de balise ou de contenu actif (script, iframe...).");
    setFieldInvalid(detailField, true);
    detailField?.focus();
    return false;
  }
  setFieldInvalid(detailField, false);
  state.maturiteDetail = detailValue;
  clearError();
  return true;
}

/** Une étape à enjeu d'éligibilité a-t-elle bien reçu une réponse avant de continuer ? */
function isGatingStepAnswered(step: number): boolean {
  if (step === 1) return state.collaborateurs !== undefined;
  if (step === 2) return state.wallonie !== undefined;
  if (step === 3) return state.chiffreAffaires !== undefined;
  if (step === 7) return state.ouvertureAcces !== undefined;
  return true;
}

/**
 * Met en évidence (bordure rouge) les champs nom/email/téléphone manquants ou mal
 * formés, et place le focus sur le premier à corriger — le message générique reste
 * affiché en complément (jamais la couleur seule pour signaler l'erreur).
 */
function highlightInvalidContactFields(contact: ContactFields): void {
  const nomField = document.getElementById("f-nom");
  const fonctionField = document.getElementById("f-fonction");
  const emailField = document.getElementById("f-email");
  const telField = document.getElementById("f-tel");

  const nomInvalid = !contact.nom || !NAME_PATTERN.test(contact.nom);
  const fonctionInvalid = Boolean(contact.fonction) && NO_ACTIVE_MARKUP_PATTERN.test(contact.fonction ?? "");
  const emailInvalid = !contact.email || !EMAIL_PATTERN.test(contact.email);
  const telInvalid = !contact.telephone || !PHONE_PATTERN.test(contact.telephone);
  setFieldInvalid(nomField, nomInvalid);
  setFieldInvalid(fonctionField, fonctionInvalid);
  setFieldInvalid(emailField, emailInvalid);
  setFieldInvalid(telField, telInvalid);

  if (nomInvalid) {
    nomField?.focus();
  } else if (fonctionInvalid) {
    fonctionField?.focus();
  } else if (emailInvalid) {
    emailField?.focus();
  } else if (telInvalid) {
    telField?.focus();
  }
}

function bindNavButtons(): void {
  document.querySelectorAll<HTMLElement>("[data-next]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const current = Number(btn.dataset.next);

      if (current === 4 && !validateSecteurStep()) return;
      if (current === 5 && !validateProblemeStep()) return;
      if (current === 6 && !validateMaturiteStep()) return;

      if (GATING_STEPS.has(current)) {
        if (!isGatingStepAnswered(current)) {
          showError("Merci de répondre à cette question avant de continuer.");
          return;
        }
        const motif = getIneligibilityMotif(state);
        if (motif) {
          showIneligibleScreen(motif);
          pushNavState({ page: "formulaire", ineligible: motif });
          return;
        }
      }

      clearError();
      nextStep(current);
    });
  });
  // "Retour" appelle directement l'historique du navigateur plutôt qu'une fonction dédiée :
  // l'étape précédente a forcément été poussée par `nextStep`/`showIneligibleScreen` juste
  // avant, donc `history.back()` y ramène exactement, sans dupliquer la logique de navigation
  // ni risquer une divergence entre ce bouton et le vrai bouton "Précédent" du navigateur.
  document.querySelectorAll<HTMLElement>("[data-prev]").forEach((btn) => {
    btn.addEventListener("click", () => {
      window.history.back();
    });
  });
}

function readContactFields(): ContactFields {
  const nom = (document.getElementById("f-nom") as HTMLInputElement | null)?.value.trim() ?? "";
  const fonction = (document.getElementById("f-fonction") as HTMLInputElement | null)?.value.trim() ?? "";
  const email = (document.getElementById("f-email") as HTMLInputElement | null)?.value.trim() ?? "";
  const telephone = (document.getElementById("f-tel") as HTMLInputElement | null)?.value.trim() ?? "";
  const consentement = (document.getElementById("f-consent") as HTMLInputElement | null)?.checked ?? false;
  return { nom, fonction, email, telephone, consentement };
}

function showError(message: string): void {
  const el = document.getElementById("formError");
  if (el) el.textContent = message;
}

function clearError(): void {
  showError("");
}

async function handleSubmit(): Promise<void> {
  clearError();

  const contact = readContactFields();
  const validationError = getFormValidationError(state, contact);
  if (validationError) {
    showError(validationError);
    highlightInvalidContactFields(contact);
    return;
  }

  setFieldInvalid(document.getElementById("f-nom"), false);
  setFieldInvalid(document.getElementById("f-fonction"), false);
  setFieldInvalid(document.getElementById("f-email"), false);
  setFieldInvalid(document.getElementById("f-tel"), false);

  const turnstileToken = getTurnstileToken("turnstile-widget");
  if (!turnstileToken) {
    showError("Merci de valider la vérification anti-robot avant d'envoyer.");
    return;
  }

  const payload: LeadCreatePayload = { ...buildLeadPayload(state, contact), turnstileToken };

  const submitBtn = document.getElementById("submitBtn") as HTMLButtonElement | null;
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = "Envoi en cours...";
  }

  try {
    await createLead(payload);
    nextStep(8);
  } catch (err) {
    resetTurnstileWidget("turnstile-widget");
    if (err instanceof ApiError) {
      showError(err.message);
    } else {
      showError("Impossible d'envoyer votre demande pour le moment. Merci de réessayer.");
      console.error(err);
    }
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = "Envoyer ma demande →";
    }
  }
}

/**
 * Soumission du mini-formulaire de contact affiché sur l'écran d'inéligibilité. Envoie un
 * payload minimal (email + consentement + réponses déjà connues) et remplace le formulaire
 * par un message de remerciement au lieu de rejoindre l'écran de succès du parcours normal
 * (le message "premier échange sous 48h" ne s'applique pas à ce cas).
 */
async function handleIneligibleSubmit(): Promise<void> {
  clearError();

  const emailInput = document.getElementById("ineligible-email") as HTMLInputElement | null;
  const consentInput = document.getElementById("ineligible-consent") as HTMLInputElement | null;
  const email = emailInput?.value.trim() ?? "";
  const consentement = consentInput?.checked ?? false;

  const validationError = getIneligibleFormValidationError(email, consentement);
  if (validationError) {
    showError(validationError);
    setFieldInvalid(emailInput, !email || !EMAIL_PATTERN.test(email));
    return;
  }
  setFieldInvalid(emailInput, false);

  const turnstileToken = getTurnstileToken("turnstile-widget-ineligible");
  if (!turnstileToken) {
    showError("Merci de valider la vérification anti-robot avant d'envoyer.");
    return;
  }

  const payload: LeadCreatePayload = { ...buildIneligibleLeadPayload(state, email, consentement), turnstileToken };

  const submitBtn = document.getElementById("ineligibleSubmitBtn") as HTMLButtonElement | null;
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = "Envoi en cours...";
  }

  try {
    await createLead(payload);
    document.getElementById("ineligibleForm")?.classList.add("visually-hidden");
    document.getElementById("ineligibleThanks")?.classList.remove("visually-hidden");
  } catch (err) {
    resetTurnstileWidget("turnstile-widget-ineligible");
    if (err instanceof ApiError) {
      showError(err.message);
    } else {
      showError("Impossible d'envoyer votre demande pour le moment. Merci de réessayer.");
      console.error(err);
    }
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = "Être recontacté →";
    }
  }
}

/** Câble les interactions du formulaire (options, navigation, soumission). */
export function initFormulairePage(): void {
  bindOptionGroups();
  bindNavButtons();
  clearFieldInvalidOnInput("secteur-autre");
  clearFieldInvalidOnInput("besoin");
  clearFieldInvalidOnInput("maturite-detail-avere");
  clearFieldInvalidOnInput("maturite-detail-experience");
  clearFieldInvalidOnInput("f-nom");
  clearFieldInvalidOnInput("f-fonction");
  clearFieldInvalidOnInput("f-email");
  clearFieldInvalidOnInput("f-tel");
  clearFieldInvalidOnInput("ineligible-email");

  const submitBtn = document.getElementById("submitBtn");
  submitBtn?.addEventListener("click", handleSubmit);

  const ineligibleSubmitBtn = document.getElementById("ineligibleSubmitBtn");
  ineligibleSubmitBtn?.addEventListener("click", handleIneligibleSubmit);

  // Motif 'acces_refuse' uniquement : permet de reconsidérer sa réponse plutôt que de
  // quitter le formulaire (ce n'est pas une contrainte objective comme les 3 autres motifs).
  // `history.back()` plutôt que `showFormStep(7)` en dur : l'écran d'inéligibilité pour ce
  // motif n'est jamais atteint que depuis l'étape 7 (voir GATING_STEPS), donc l'historique y
  // ramène toujours correctement, sans supposer un numéro d'étape figé.
  document.getElementById("ineligibleBackToForm")?.addEventListener("click", () => {
    window.history.back();
  });

  onPopState(restoreFormView);
}

/**
 * Restitue l'écran exact (étape numérotée ou écran d'inéligibilité) qu'un retour/avance
 * navigateur cible, à partir de l'état poussé par `nextStep`/`showIneligibleScreen`. Pure
 * restitution visuelle : ne revalide rien, l'état était déjà valide au moment du push.
 */
function restoreFormView(navState: NavState): void {
  if (navState.page !== "formulaire") return;
  if (navState.ineligible) {
    showIneligibleScreen(navState.ineligible as MotifIneligibilite);
  } else if (navState.step) {
    showFormStep(navState.step);
  }
}

/** Remet le formulaire à l'étape 1 et efface le message d'erreur — appelé à chaque retour sur la page. */
export function resetFormulairePage(): void {
  resetState();
  showFormStep(1);
  // Remplace (n'empile pas) : établit l'étape 1 comme base de l'historique du formulaire pour
  // cette visite, sans créer d'entrée fantôme en plus de celle déjà poussée par `goTo`.
  pushNavState({ page: "formulaire", step: 1 }, { replace: true });
  clearError();
  setFieldInvalid(document.getElementById("secteur-autre"), false);
  setFieldInvalid(document.getElementById("besoin"), false);
  setFieldInvalid(document.getElementById("maturite-detail-avere"), false);
  setFieldInvalid(document.getElementById("maturite-detail-experience"), false);
  setFieldInvalid(document.getElementById("f-nom"), false);
  setFieldInvalid(document.getElementById("f-fonction"), false);
  setFieldInvalid(document.getElementById("f-email"), false);
  setFieldInvalid(document.getElementById("f-tel"), false);
  setFieldInvalid(document.getElementById("ineligible-email"), false);

  document.getElementById("secteurAutreBox")?.classList.add("visually-hidden");
  document.getElementById("problemeAutreBox")?.classList.add("visually-hidden");
  document.getElementById("maturiteAvereBox")?.classList.add("visually-hidden");
  document.getElementById("maturiteExperienceBox")?.classList.add("visually-hidden");
  const secteurAutreInput = document.getElementById("secteur-autre") as HTMLInputElement | null;
  if (secteurAutreInput) secteurAutreInput.value = "";
  const besoinTextarea = document.getElementById("besoin") as HTMLTextAreaElement | null;
  if (besoinTextarea) besoinTextarea.value = "";
  const maturiteAvereTextarea = document.getElementById("maturite-detail-avere") as HTMLTextAreaElement | null;
  if (maturiteAvereTextarea) maturiteAvereTextarea.value = "";
  const maturiteExperienceTextarea = document.getElementById(
    "maturite-detail-experience"
  ) as HTMLTextAreaElement | null;
  if (maturiteExperienceTextarea) maturiteExperienceTextarea.value = "";

  document.querySelectorAll<HTMLInputElement>('.fstep input[type="radio"]:checked').forEach((input) => {
    input.checked = false;
  });

  const ineligibleEmail = document.getElementById("ineligible-email") as HTMLInputElement | null;
  if (ineligibleEmail) ineligibleEmail.value = "";
  const ineligibleConsent = document.getElementById("ineligible-consent") as HTMLInputElement | null;
  if (ineligibleConsent) ineligibleConsent.checked = false;
  document.getElementById("ineligibleForm")?.classList.remove("visually-hidden");
  document.getElementById("ineligibleThanks")?.classList.add("visually-hidden");

  resetTurnstileWidget("turnstile-widget");
  resetTurnstileWidget("turnstile-widget-ineligible");
}
